/**
 * serializeDealForm — local stand-in for the library's `fromFormValues`.
 *
 * ⚠️ LIBRARY GAP (flag for component-agent): the published
 * `@warwick-investment-group/wig-react-library` type barrel
 * (`types/index.d.ts` line 2) advertises a named export `fromFormValues`,
 * and `types/composite/WigMetadataForm.d.ts` documents it as the supported
 * way for a consumer that owns submit to serialize `WigMetadataForm` state
 * back into an API body. But the SHIPPED runtime bundles
 * (`dist/esm/index.js` -> `dist/esm/bundle.esm.js`, and the CJS bundle) do
 * NOT include `fromFormValues` (nor `WigMetadataFormItem`) in their
 * top-level `export { ... }` block — they exist only as internal webpack
 * module members. So
 *   `import { fromFormValues } from "@warwick-investment-group/wig-react-library"`
 * resolves to `undefined` at runtime in this JS app, with no compile-time
 * error (TS would catch it; .jsx does not). This is the exact class of
 * "looks fine, silently no-ops" defect the consumer guidelines warn about.
 *
 * Until the library re-exports `fromFormValues`, this util reproduces the
 * documented contract so deal CREATE / EDIT works:
 *   - `Date` / `Timestamp` fields: dayjs instance -> ISO-ish string
 *     ("YYYY-MM-DD" for Date, full ISO for Timestamp). Null/empty -> null.
 *   - `Numeric` fields: coerce numeric strings to Number; blank -> null.
 *   - Everything else: passed through; "" -> null so we don't send empty
 *     strings for optional columns.
 *   - Only fields the form could legitimately edit are emitted: hidden
 *     (`hide`) and `editable === false` rows are dropped, matching the
 *     library's default `fieldFilter` (`!hide && editable !== false`).
 *     This keeps the body free of server-managed columns (id, created_by,
 *     ...), so the backend's `extra='forbid'` 422 guard is never tripped
 *     by audit fields that AntD may have registered.
 *
 * When the library fix lands, replace calls to this with the imported
 * `fromFormValues` and delete this file.
 */

// Duck-type a dayjs instance without importing dayjs directly (it's only a
// transitive dep via antd). dayjs objects expose a `format` method and the
// `$isDayjsObject` marker (dayjs >= 1.11); older builds expose `$d`.
const isDayjs = (v) =>
  !!v &&
  typeof v === "object" &&
  typeof v.format === "function" &&
  (v.$isDayjsObject === true || "$d" in v);

const DATE_TYPES = new Set(["Date", "Timestamp"]);

/**
 * @param {Record<string, unknown>} values   — `form.getFieldsValue(true)`
 * @param {Array<object>} metadata           — UIMetadata rows for the table
 * @param {(field: string, meta: object) => boolean} [canWrite]
 *        — optional per-field write predicate (see `canEditField` in
 *        utils/permissions). A field it rejects is omitted from the body
 *        entirely rather than sent as an unchanged echo.
 *
 *        This matters because the body is built from the WHOLE form, not a
 *        diff: an empty date serializes as an explicit `null`, so a field the
 *        caller may not write would otherwise appear in the PUT and the
 *        server's field-level guard would 403 the entire save — naming
 *        fields the user never touched.
 * @returns {Record<string, unknown>}         — API-ready body
 */
export const serializeDealForm = (values, metadata, canWrite) => {
  if (!values || typeof values !== "object") return {};
  const metaByField = new Map();
  for (const m of Array.isArray(metadata) ? metadata : []) {
    if (m && m.field) metaByField.set(m.field, m);
  }

  const body = {};
  for (const [field, raw] of Object.entries(values)) {
    const meta = metaByField.get(field);
    // Drop fields with no metadata row, or that the form should never write
    // (hidden / non-editable). Mirrors WigMetadataForm's default fieldFilter.
    if (!meta) continue;
    if (meta.hide === true) continue;
    if (meta.editable === false) continue;
    // Role-gated columns the caller may not write.
    if (typeof canWrite === "function" && !canWrite(field, meta)) continue;

    const dt = meta.data_type;

    if (DATE_TYPES.has(dt)) {
      if (raw == null || raw === "") {
        body[field] = null;
      } else if (isDayjs(raw)) {
        body[field] =
          dt === "Date" ? raw.format("YYYY-MM-DD") : raw.toISOString();
      } else {
        // Already a string/Date — pass through unchanged.
        body[field] = raw;
      }
      continue;
    }

    if (dt === "Numeric") {
      if (raw == null || raw === "") {
        body[field] = null;
      } else if (typeof raw === "number") {
        body[field] = raw;
      } else {
        const n = Number(raw);
        body[field] = Number.isFinite(n) ? n : raw;
      }
      continue;
    }

    // Text / LongText / Lookup / Boolean / etc. Normalize empty string to
    // null so optional columns aren't sent as "".
    body[field] = raw === "" ? null : raw;
  }

  return body;
};

export default serializeDealForm;
